<template>
    <div>
        <b-alert :show="form.step == 1" variant="info">
            <p>
                Welcome to CareExchange, an electronic reporting system that eliminates the need for care recipients
                and providers to maintain paper timesheets and submit them to insurance carriers. You and your
                providers may use CareExchange at no cost.
            </p>

            <p>
                Please follow the steps below to complete the enrollment process.
            </p>
        </b-alert>

        <!-- STEP 1 : ACCOUNT -->
        <b-card class="step_1" v-if="form.step == 1"
            header="Step 1: Enter and/or confirm your email address and create a password"
            header-bg-variant="info"
            header-text-variant="white"
        >
            <form @submit.prevent="submitStep()" @keydown="form.clearError($event.target.name)">
                <b-row>
                    <b-col lg="6" class="with-padding-bottom">
                        <b-form-group label="Email Address" label-for="email">
                            <b-form-input
                                    id="email"
                                    name="email"
                                    type="email"
                                    v-model="form.email"
                            >
                            </b-form-input>
                            <input-help :form="form" field="email" text="Confirm your email address.  Ex: user@domain.com"></input-help>
                        </b-form-group>
                        <b-form-group label="Password" label-for="password">
                            <b-form-input
                                    id="password"
                                    name="password"
                                    type="password"
                                    v-model="form.password"
                                    required
                            >
                            </b-form-input>
                            <input-help :form="form" field="password" text="Password must be at least 8 characters and contain 1 uppercase letter and 1 number."></input-help>
                        </b-form-group>
                        <b-form-group label="Confirm Password" label-for="password_confirmation">
                            <b-form-input
                                    id="password_confirmation"
                                    name="password_confirmation"
                                    type="password"
                                    v-model="form.password_confirmation"
                                    required
                            >
                            </b-form-input>
                            <input-help :form="form" field="password_confirmation" text="Re-enter the above password for confirmation."></input-help>
                        </b-form-group>

                        <b-button variant="success" type="submit" :disabled="busy">
                            <i class="fa fa-spin fa-spinner" v-show="busy"></i> Save and Continue to Step 2
                        </b-button>
                    </b-col>
                </b-row>
            </form>
        </b-card>

        <!-- STEP 2 : PROVIDERS -->
        <b-card class="step_2" v-if="form.step == 2"
            header="Step 2: Verify provider information"
            header-bg-variant="info"
            header-text-variant="white"
        >
            <form @submit.prevent="submitStep()" @keydown="form.clearError($event.target.name)">
                <b-row>
                    <b-col lg="12" class="with-padding-bottom">
                        <p>
                            <strong>Please confirm the caregiver(s) that service you along with their agreed hourly rate.  If your caregiver's hourly rate is not correct, please update it here.</strong>
                        </p>
                        <div v-if="hasNoCaregivers" class="my-5 text-center">
                            <h4>You don't have any assigned Providers.  Please contact Care Exchange.</h4>
                        </div>
                        <div v-for="(caregiver, i) in form.caregivers" :key="i">
                            <hr />
                            <strong>Provider {{ i+1 }}</strong>
                            <b-row>
                                <b-col>
                                    <b-form-group label="First Name">
                                        <b-form-input type="text"
                                                        v-model="caregiver.firstname"
                                                        disabled
                                        />
                                    </b-form-group>
                                </b-col>
                                <b-col>
                                    <b-form-group label="Last Name">
                                        <b-form-input type="text"
                                                        v-model="caregiver.lastname"
                                                        disabled
                                        />
                                    </b-form-group>
                                </b-col>
                                <b-col>
                                    <b-form-group label="Email Address">
                                        <b-form-input type="email"
                                                        v-model="caregiver.email"
                                                        :readonly="caregiverHasEmail(caregiver.id)"
                                        />
                                    </b-form-group>
                                </b-col>
                                <b-col>
                                    <b-form-group label="Hourly Rate">
                                        <b-form-input type="number"
                                                        step="any"
                                                        v-model="caregiver.rate"
                                        />
                                        <input-help :form="form" :field="`caregivers.${i}.rate`" text=""></input-help>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </div>
                    </b-col>

                    <b-col lg="6" class="mt-2 with-padding-bottom">
                        <b-button 
                                variant="info" 
                                :disabled="busy"
                                @click="form.step = 1"
                            >
                            <i class="fa fa-spin fa-spinner" v-show="busy"></i> Go Back
                        </b-button>
                    </b-col>
                    <b-col lg="6" class="mt-2 with-padding-bottom text-right">
                        <b-button variant="success" type="submit" :disabled="busy || hasNoCaregivers">
                            <i class="fa fa-spin fa-spinner" v-show="busy"></i> Save and Continue to Step 3
                        </b-button>
                    </b-col>
                </b-row>
            </form>
        </b-card>
        
        <!-- STEP 3 : ADDRESS -->
        <b-card class="step_3" v-if="form.step == 3"
            header="Step 3: Enter the address and landline phone number where you receive care"
            header-bg-variant="info"
            header-text-variant="white"
        >
            <form @submit.prevent="submitStep()" @keydown="form.clearError($event.target.name)">
                <b-row>
                    <b-col lg="6">

                        <b-form-group label="Service Address">
                            <b-row class="mb-3">
                                <b-col sm="12">
                                    <b-form-input
                                            id="address1"
                                            name="address1"
                                            type="text"
                                            v-model="form.address1"
                                            placeholder="Address Line 1"
                                    >
                                    </b-form-input>
                                    <input-help :form="form" field="address1" text="Confirm your street address."></input-help>
                                </b-col>
                            </b-row>
                            <b-row class="mb-3">
                                <b-col sm="12">
                                    <b-form-input
                                            id="address2"
                                            name="address2"
                                            type="text"
                                            v-model="form.address2"
                                            placeholder="Address Line 2"
                                    >
                                    </b-form-input>
                                    <input-help :form="form" field="address2" text="Confirm your address second line (apartment number or other)."></input-help>
                                </b-col>
                            </b-row>
                            <b-row class="mb-3">
                                <b-col sm="5" xs="6">
                                    <b-form-input
                                            id="city"
                                            name="city"
                                            type="text"
                                            v-model="form.city"
                                            placeholder="City"
                                    >
                                    </b-form-input>
                                    <input-help :form="form" field="city" text="Confirm your city."></input-help>
                                </b-col>
                                <b-col sm="3" xs="3">
                                    <b-form-select name="state" :options="states.getOptions()" v-model="form.state" />
                                    <input-help :form="form" field="state" text="Confirm your state."></input-help>
                                </b-col>
                                <b-col sm="4" xs="3">
                                    <b-form-input
                                            id="zip"
                                            name="zip"
                                            type="text"
                                            v-model="form.zip"
                                            placeholder="Zip Code"
                                    >
                                    </b-form-input>
                                    <input-help :form="form" field="zip" text="Confirm your zip code."></input-help>
                                </b-col>
                            </b-row>
                        </b-form-group>
                    </b-col>

                    <b-col lg="6">
                        <b-form-group label="Service Address Phone Number" label-for="phone_number">
                            <mask-input v-model="form.phone_number" id="phone_number" type="phone"></mask-input>
                            <input-help :form="form" field="phone_number" text="Confirm the landline phone number for your service address."></input-help>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col lg="12">
                        <terms-of-use />
                        <b-form-checkbox v-model="form.terms" value="1">I agree to the above Terms of Use</b-form-checkbox>
                        <input-help :form="form" field="terms" text="" class="mb-3"></input-help>
                    </b-col>
                    <!--<b-col lg="12">-->
                        <!-- <b-form-checkbox name="tcpa_opt_in" v-model="form.tcpa_opt_in" value="1">I agree to the above Terms of Use</b-form-checkbox> -->
                    <!--</b-col>-->
                    <b-col lg="6" class="mt-2 with-padding-bottom">
                        <b-button 
                                variant="info" 
                                :disabled="busy"
                                @click="form.step = 2"
                            >
                            <i class="fa fa-spin fa-spinner" v-show="busy"></i> Go Back
                        </b-button>
                    </b-col>
                    <b-col lg="6" class="mt-2 with-padding-bottom text-right">
                        <b-button variant="success" type="submit" :disabled="busy">
                            <i class="fa fa-spin fa-spinner" v-show="busy"></i> Accept &amp; Complete Enrollment
                        </b-button>
                    </b-col>
                </b-row>
            </form>
        </b-card>
    </div>
</template>

<script>
    import States from "../classes/States";
    import FormatsListData from "../mixins/FormatsListData";

    export default {
        mixins: [FormatsListData],

        props: {
            'token': String,
            'email': String,
            'client': {},
            'phoneNumber': {},
            'address': {},
            'caregivers': Array,
        },

        data() {
            return {
                form: new Form({
                    step: 1,
                    
                    // step 1
                    email: this.email,
                    password: null,
                    password_confirmation: null,
                    
                    // step 2
                    caregivers: this.caregivers.map(item => {
                        item.email = this.formatEmail(item.email);
                        item.original_rate = item.rate;
                        return item;
                    }),

                    // step 3
                    address1: this.address.address1,
                    address2: this.address.address2,
                    city: this.address.city,
                    state: this.address.state,
                    zip: this.address.zip,
                    phone_number: this.phoneNumber,
                    terms: 0,
                }),
                busy: false,
                states: new States(),
            }
        },

        computed: {
            carrierName() {
                return this.client.carrier.name;
            },

            hasNoCaregivers() {
                return ! this.form.caregivers || this.form.caregivers.length == 0;
            }
        },

        mounted() {
            this.form.step = 1;
            if (this.client.enrollment_status == 'enrolled') {
                if (this.client.user.account_setup == 1) {
                    this.form.step = 2;
                } else if (this.client.user.account_setup == 2) {
                    this.form.step = 3;
                }
            } else {
                if (this.client.enrollment_status == 'step2') {
                    this.form.step = 2;
                } else if (this.client.enrollment_status == 'step3') {
                    this.form.step = 3;
                }
            }
        },
        
        methods: {
            caregiverHasEmail(id) {
                return true; // always disable this check for now
                let caregiver = this.form.caregivers.find(item => item.id === id);
                if (caregiver && caregiver.email) return true;
                return false;
            },

            submitStep() {
                this.busy = true;
                this.form.post('/enroll/' + this.token)
                    .then( ({ data }) => {
                        if (this.form.step == 3) {
                            window.location = '/enroll/saved?username=' + encodeURIComponent(this.form.email);
                        } else {
                            this.form.step++;
                            this.busy = false
                        }
                    })
                    .catch(e => {
                        this.busy = false
                    });
            },
        },
    }
</script>
